import imageCompression from 'browser-image-compression';

export interface UseFileCompression {
  /**
   * Returns either the original File (videos, documents, non-photo types) or a
   * compressed/converted File (HEIC -> JPEG, then JPEG iteratively shrunk to
   * target). Always returns a File (never a Blob).
   *
   * Throws nothing visible: on internal failure the original File is returned
   * and a console.warn is emitted (Wave 3 will still attempt the upload -- the
   * server-side extension check guards correctness).
   */
  compressIfPhoto(file: File): Promise<File>;
}

const PHOTO_COMPRESSION_OPTIONS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 2048,
  initialQuality: 0.8,
  // Main thread, NOT a web worker. browser-image-compression's worker mode
  // importScripts() the library from the jsDelivr CDN at runtime, which the
  // app CSP (script-src) blocks — and pulling app code from a public CDN is a
  // supply-chain risk the CSP exists to prevent. The bundled main-thread path
  // needs no network and works under the CSP; cost is brief main-thread work
  // per photo (a handful of images), which is acceptable for upload.
  useWebWorker: false,
  fileType: 'image/jpeg',
} as const;

const HEIC_MIME = /^image\/(heic|heif)/i;
const HEIC_EXT = /\.hei[cf]$/i;

/**
 * Returns true if `file` is a HEIC/HEIF image by MIME type or filename
 * extension. Exported as the single source of truth so callers
 * (e.g. WizardStepDetails) don't duplicate the regex and drift over time.
 *
 * Prefix-matches `image/heic`, `image/heif`, `image/heic-sequence`,
 * `image/heif-sequence` by design.
 */
export function isHeic(file: File): boolean {
  return HEIC_MIME.test(file.type) || HEIC_EXT.test(file.name);
}

function isPhoto(file: File): boolean {
  return (
    /^image\//.test(file.type) || /\.(jpe?g|png|heic|heif)$/i.test(file.name)
  );
}

/**
 * Wraps a Blob as a File if it isn't one already.
 */
function asFile(input: Blob | File, name: string): File {
  if (input instanceof File) {
    return input;
  }
  return new File([input], name, { type: input.type });
}

export function useFileCompression(): UseFileCompression {
  async function compressIfPhoto(file: File): Promise<File> {
    if (!isPhoto(file)) {
      return file;
    }

    try {
      let working: File = file;

      if (isHeic(file)) {
        const { heicTo } = await import('heic-to');
        const convertedBlob = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: 0.9,
        });
        working = asFile(
          convertedBlob,
          file.name.replace(/\.hei[cf]$/i, '.jpg'),
        );
      }

      const compressed = await imageCompression(
        working,
        PHOTO_COMPRESSION_OPTIONS,
      );
      return asFile(compressed, working.name);
    } catch (err) {
      console.warn(
        '[useFileCompression] compression failed, returning original',
        err,
      );
      return file;
    }
  }

  return { compressIfPhoto };
}
